import { useAuth } from "../auth/AuthContext";

/**
 * PRD-005 deny page. Shown when the user is authenticated but lacks the
 * `Subject_Read` permission on their patient-registry session — either
 * caught client-side by `<AuthorizedGate>` or via a server 403 surfaced
 * through the React Query global error boundary.
 *
 * Copy is verbatim from PRD-005 §SPA DoD (vetted by customer-signal
 * review for the multi-tenant CSM context — separate per-tenant users
 * means a deny on one tenant must not imply the app is broken globally).
 *
 * The "Sign out" CTA clears the cached JWT and re-surfaces the paste
 * modal, mirroring the existing PRD-002 logout shape. The user pastes
 * the *new* token they got after their tenant admin granted Subject_Read.
 */
export function DenyPage() {
    const { logout } = useAuth();
    return (
        <div className="empty-state" data-testid="authz-deny-page" role="alert">
            <div style={{ maxWidth: 640, lineHeight: 1.5 }}>
                <h2 style={{ marginTop: 0 }}>
                    You don&apos;t have access to bulk checklist export on this tenant.
                </h2>
                <p>
                    This feature requires the <code>Subject_Read</code> permission on your
                    patient-registry session for the tenant you&apos;re currently using.
                </p>
                <p style={{ marginBottom: 8 }}>To gain access:</p>
                <ol>
                    <li>
                        Ask your patient-registry tenant admin to grant <code>Subject_Read</code>.
                        If you&apos;re an internal xCures user and unsure who that is, ask in{" "}
                        <code>#product-ops</code>.
                    </li>
                    <li>
                        Once they&apos;ve updated your role,{" "}
                        <strong>return to your patient-registry session and refresh / re-login</strong>{" "}
                        to mint a new token that includes the updated permissions. (The token you
                        previously pasted has your old permissions baked in.)
                    </li>
                    <li>
                        Copy the new token from your patient-registry session and{" "}
                        <strong>paste it here</strong> by clicking &ldquo;Sign out&rdquo; below and
                        re-authorizing.
                    </li>
                </ol>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={logout}
                    data-testid="authz-deny-signout"
                    style={{ marginTop: 12 }}
                >
                    <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" /> Sign
                    out
                </button>
            </div>
        </div>
    );
}
